/**
 * IAN Assist — Workflow DAG Schema
 *
 * A Workflow is a Directed Acyclic Graph (DAG) of sub-workflow nodes.
 * The Master Dispatcher executes the DAG, threading outputs from one
 * node into the inputs of dependent nodes.
 *
 * Node shape:
 *   {
 *     id: string                       // unique within DAG
 *     wf: string                       // sub-workflow id e.g. 'wf.summarize'
 *     after?: string[]                 // node ids that must complete first (sequential)
 *     parallel_group?: string          // nodes sharing a group run concurrently
 *     input?: Object                   // static input params
 *     input_from?: Object              // template strings, e.g. { text: '{{node.field}}' }
 *     trigger_when?: Object            // optional condition; node skipped if false
 *   }
 *
 * Workflow shape:
 *   {
 *     id: string
 *     name: string
 *     persona?: string                 // 'claims-adjuster' | 'broker' | ...
 *     intent_match?: string[]          // chat phrases that auto-pick this DAG
 *     dag: Node[]
 *     output_cards: string[]           // node ids whose outputs become user-visible cards
 *   }
 */

export const SUB_WORKFLOWS = {
  SUMMARIZE: 'wf.summarize',
  TRANSLATE: 'wf.translate',
  DRAFT_REPLY: 'wf.draft_reply',
  REPHRASE: 'wf.rephrase',
  COMPOSE: 'wf.compose',
  ACTIONS: 'wf.actions',
  DETECT_LANGUAGE: 'wf.detect_language',
  CLASSIFY_INTENT: 'wf.classify_intent',
  EXECUTE_ACTION: 'wf.execute_action',
  AGGREGATE: 'wf.aggregate',
};

const VALID_WF_IDS = new Set(Object.values(SUB_WORKFLOWS));

/**
 * Validate a DAG. Returns { ok: boolean, errors: string[] }.
 */
export function validateDag(dag) {
  const errors = [];
  if (!Array.isArray(dag)) {
    return { ok: false, errors: ['dag must be an array'] };
  }
  const ids = new Set();
  for (const node of dag) {
    if (!node.id) errors.push('node missing id');
    if (ids.has(node.id)) errors.push(`duplicate id: ${node.id}`);
    ids.add(node.id);
    if (!node.wf) errors.push(`node ${node.id} missing wf`);
    if (node.wf && !VALID_WF_IDS.has(node.wf)) {
      errors.push(`node ${node.id} unknown wf: ${node.wf}`);
    }
  }
  for (const node of dag) {
    if (node.after) {
      for (const dep of node.after) {
        if (!ids.has(dep)) errors.push(`node ${node.id} depends on unknown ${dep}`);
      }
    }
  }
  // Cycle detection (Kahn's algorithm)
  const indeg = new Map();
  const adj = new Map();
  for (const n of dag) {
    indeg.set(n.id, 0);
    adj.set(n.id, []);
  }
  for (const n of dag) {
    for (const dep of n.after || []) {
      indeg.set(n.id, (indeg.get(n.id) || 0) + 1);
      if (!adj.has(dep)) continue; // unknown dep already reported above
      adj.get(dep).push(n.id);
    }
  }
  const queue = [...indeg.entries()].filter(([, d]) => d === 0).map(([k]) => k);
  let visited = 0;
  while (queue.length) {
    const cur = queue.shift();
    visited++;
    for (const next of adj.get(cur) || []) {
      indeg.set(next, indeg.get(next) - 1);
      if (indeg.get(next) === 0) queue.push(next);
    }
  }
  if (visited !== dag.length) errors.push('cycle detected in DAG');

  return { ok: errors.length === 0, errors };
}

/**
 * Topological order grouped by execution wave.
 * Each wave can run in parallel; waves run sequentially.
 */
export function topoWaves(dag) {
  const indeg = new Map();
  const adj = new Map();
  const byId = new Map();
  for (const n of dag) {
    indeg.set(n.id, (n.after || []).length);
    adj.set(n.id, []);
    byId.set(n.id, n);
  }
  for (const n of dag) {
    for (const dep of n.after || []) {
      adj.get(dep).push(n.id);
    }
  }
  const waves = [];
  let frontier = [...indeg.entries()].filter(([, d]) => d === 0).map(([k]) => byId.get(k));
  while (frontier.length) {
    waves.push(frontier);
    const next = [];
    for (const node of frontier) {
      for (const downstream of adj.get(node.id)) {
        indeg.set(downstream, indeg.get(downstream) - 1);
        if (indeg.get(downstream) === 0) next.push(byId.get(downstream));
      }
    }
    frontier = next;
  }
  return waves;
}

/**
 * Substitute {{node.field}} in input_from templates against an outputs map.
 */
export function resolveInputFrom(template, outputs) {
  if (!template || typeof template !== 'object') return {};
  const resolved = {};
  for (const [k, v] of Object.entries(template)) {
    if (typeof v === 'string') {
      resolved[k] = v.replace(/\{\{([\w.]+)\}\}/g, (_, path) => {
        const parts = path.split('.');
        let cur = outputs;
        for (const p of parts) {
          if (cur == null) return '';
          cur = cur[p];
        }
        return cur == null ? '' : String(cur);
      });
    } else {
      resolved[k] = v;
    }
  }
  return resolved;
}
